/**
 * Image fallback utility for handling failed image loads
 */

/* eslint-disable react-refresh/only-export-components */
import React, { useState, useEffect, forwardRef } from 'react';
import { DEFAULT_FALLBACK_IMAGE } from './imageFallbackUtils';

// Image fallback hook
export const useImageFallback = (src: string, fallbackSrc?: string) => {
  const [imageSrc, setImageSrc] = useState(src);
  const [hasError, setHasError] = useState(false);

  const handleError = () => {
    if (!hasError) {
      setHasError(true);
      setImageSrc(fallbackSrc || DEFAULT_FALLBACK_IMAGE);
    }
  };

  const handleLoad = () => {
    setHasError(false);
  };

  // Reset when src changes
  useEffect(() => {
    setImageSrc(src);
    setHasError(false);
  }, [src]);

  return {
    src: imageSrc,
    onError: handleError,
    onLoad: handleLoad,
    hasError,
  };
};

// Image component with fallback
export interface ImageWithFallbackProps {
  src: string;
  alt: string;
  fallbackSrc?: string;
  className?: string;
  style?: React.CSSProperties;
  onError?: () => void;
  onLoad?: () => void;
  srcSet?: string;
  sizes?: string;
  loading?: 'lazy' | 'eager';
  decoding?: 'async' | 'sync' | 'auto';
  fetchPriority?: 'high' | 'low' | 'auto';
}

export const ImageWithFallback = forwardRef<HTMLImageElement, ImageWithFallbackProps>(
  (
    {
      src,
      alt,
      fallbackSrc,
      className,
      style,
      onError,
      onLoad,
      srcSet,
      sizes,
      loading,
      decoding,
      fetchPriority,
    },
    ref,
  ) => {
    const {
      src: imageSrc,
      onError: handleError,
      onLoad: handleLoad,
    } = useImageFallback(src, fallbackSrc);

    const handleImageError = () => {
      handleError();
      onError?.();
    };

    const handleImageLoad = () => {
      handleLoad();
      onLoad?.();
    };

    return (
      <img
        ref={ref}
        src={imageSrc}
        alt={alt}
        className={className}
        style={style}
        srcSet={srcSet}
        sizes={sizes}
        loading={loading}
        decoding={decoding}
        {...(fetchPriority && { fetchPriority })}
        onError={handleImageError}
        onLoad={handleImageLoad}
        crossOrigin="anonymous"
      />
    );
  },
);

ImageWithFallback.displayName = 'ImageWithFallback';
